/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/ScopeAgGridRowActions.tsx
 * FILE_ID: QB-AGGRID-ROWACTIONS-001
 * PURPOSE: AG-Grid cell renderer that paints per-row action buttons on
 *          the rightmost column of each real row. Today: Apply pricelist
 *          application (optional, waterproofing autofill), Duplicate,
 *          and Delete. Pad rows render nothing (they have no identity
 *          yet) — once a pad is promoted to a real row it gains its
 *          actions automatically.
 * WHY: SAM's grid surfaces per-row icons at the right edge so the
 *      estimator never has to multi-select or right-click to do the
 *      most common operations. The Apply-application button is the
 *      Bravada pricelist-takeoff hook (MR4): one click → searchable
 *      modal → backend Application.apply_application autofills the
 *      product slot columns added in MR3.
 * FLOW:
 *   ┌──────────────────────┐    ┌──────────────────────┐    ┌──────────────────────┐
 *   │ AG-Grid renders the  │ -> │ ScopeAgGridRowActions│ -> │ context.onApply…/    │
 *   │ rightmost __actions  │    │  reads params.data + │    │ onDuplicate /        │
 *   │ cell                 │    │  params.context      │    │ onDelete callbacks   │
 *   └──────────────────────┘    └──────────────────────┘    └──────────────────────┘
 * DEPENDENCIES: lucide-react (Layers, Copy, Trash2), pad-row-helpers
 * RELATED:
 *   - QB-AGGRID-COLUMNS-001 (scope-ag-grid-columns.ts) — registers the column
 *   - QB-AGGRID-LAZY-001 (lazy-aggrid-impl.tsx) — passes `context`
 *   - QB-AGGRID-APPLICATION-PICKER-001 (use-scope-ag-application-picker.ts)
 */

import { Copy, Layers, Trash2 } from 'lucide-react'
import type { QuoteLineItem } from '@/lib/quoteApi'
import { isPadRowId } from './pad-row-helpers'

export interface RowActionsContext {
  onDuplicate: (row: QuoteLineItem) => void
  onDelete: (rowId: string) => void
  /** Optional. When defined, the row exposes an "Apply pricelist
   *  application" button that opens the ApplicationSelectorModal.
   *  Left undefined for trade modes (e.g. steel/SAM) that don't yet
   *  have an application catalogue — the button hides cleanly. */
  onApplyApplication?: (row: QuoteLineItem) => void
  isEditable: boolean
}

interface ICellRendererLikeParams {
  data?: QuoteLineItem | null
  context?: RowActionsContext
}

export default function ScopeAgGridRowActions(
  params: ICellRendererLikeParams,
) {
  const row = params.data
  const ctx = params.context
  // Pad rows have no real id yet — render nothing so the cell looks
  // visually identical to the placeholder rows and doesn't tempt the
  // estimator to click into nothing.
  if (!row?.id || isPadRowId(row.id) || !ctx) return null
  if (!ctx.isEditable) return null

  // Visibility is controlled by ScopeAgGridStyles (CSS rule on
  // .ag-row:hover .qb-row-actions). Default state: low opacity so the
  // row chrome stays clean; full opacity on row hover so the action
  // is one click away.
  return (
    <div className="qb-row-actions flex items-center justify-end gap-1 h-full pr-1">
      {ctx.onApplyApplication ? (
        <button
          type="button"
          title="Apply pricelist application (autofill products + productivity)"
          onClick={(e) => {
            e.stopPropagation()
            ctx.onApplyApplication?.(row)
          }}
          className="qb-row-action-apply inline-flex items-center justify-center w-6 h-6 rounded text-indigo-500 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 hover:text-indigo-700 dark:hover:text-indigo-300"
        >
          <Layers className="w-3.5 h-3.5" />
        </button>
      ) : null}
      <button
        type="button"
        title="Duplicate row"
        onClick={(e) => {
          e.stopPropagation()
          ctx.onDuplicate(row)
        }}
        className="inline-flex items-center justify-center w-6 h-6 rounded text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-white"
      >
        <Copy className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        title="Delete row"
        onClick={(e) => {
          e.stopPropagation()
          ctx.onDelete(row.id)
        }}
        className="inline-flex items-center justify-center w-6 h-6 rounded text-slate-500 dark:text-slate-400 hover:bg-red-50 dark:hover:bg-red-950/40 hover:text-red-600 dark:hover:text-red-400"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}
